/* cf-cut-panel-truth.js — Click Fencing
 *
 * Pete's rule, settled 2026-08-25:
 *   Cut Panel mode builds a section from the MAXIMUM number of full panels, and whatever
 *   is left over becomes ONE cut panel at one end. The smallest cut panel worth making is
 *   100mm; below that the leftover stays as a small gap rather than a sliver.
 *   Where the run cannot be filled exactly, the section switches itself to Even Panels —
 *   the customer can switch back to Cut Panel and take the gap.
 *   He also asked to choose which end the cut panel sits at.
 *
 * Defect this started from (measured live, configure preview, settled reads):
 *   PerfMesh halved the last panel whenever the offcut would be under 999mm.
 *     3000mm / 2000 sheet -> drew 1425 + 1425          should be 2000 + 850
 *     5000mm / 2000 sheet -> drew 2000 + 1400 + 1400   should be 2000 + 2000 + 800
 *     7000mm / 2000 sheet -> drew 2000+2000+1375+1375  should be 2000 + 2000 + 2000 + 750
 *     9000mm / 2000 sheet -> drew 2000x3 + 1350 + 1350 should be 2000 x4 + 700
 *   Source: _calculateSpreadCore takes a `filler > 0 && filler < 999` branch that halves
 *   the span left after qty-1 full panels. Flat top and blade do not carry that branch,
 *   but this repair is written against the RESULT, not one calculator's source, so it
 *   holds for all three.
 *
 * Why the layout is corrected here and not on screen:
 *   This wraps calculateSpread on the live instance (all three aluminium calculators
 *   expose it as window.glassCalculator). That result is the one object the drawing, the
 *   breakdown, the saved quote and the cart are all built from, so they cannot disagree.
 *   An earlier patch corrected a rendered screen instead and over-charged; never again.
 *
 * The arithmetic:
 *   n panels need n+1 posts, plus one more for a gate and one for a rake. Panel count and
 *   post count therefore move together, so a rule written against the drawn span alone
 *   quietly contradicts itself. Everything below is worked out from the section length and
 *   the result's own deductions, and the rebuilt result is checked to sum back to the
 *   section length before it is allowed out.
 *
 * Safety:
 *   - Stands down unless it can prove its own arithmetic (see `verify`).
 *   - Leaves a last panel WIDER than the sheet alone: that is Pete's separate rule that an
 *     oversized panel bills the next kit up, already correct.
 *   - Leaves gate and rake sections' fixed widths untouched; only panels move.
 *   - Idempotent: a corrected layout is already canonical, so a second pass changes nothing.
 *   - Every entry point in try/catch; worst case is "no correction applied".
 *
 * Also: puts the Configuration Summary rows in Pete's order —
 *   Length, Panels, Rake, Posts, Gate, Hinge Type, Latch Type.
 */
(function () {
  'use strict';
  if (window.__cfCutPanelTruthInstalled) return;
  window.__cfCutPanelTruthInstalled = 1;

  var LOG = '[cf-cut-panel-truth]';
  var MIN_CUT = 100;               // Pete, 2026-08-25: smallest cut panel worth making
  var SIDE = {};                   // sectionNum -> 'end' | 'start'
  var SWITCHED = {};               // sectionNum -> the length we already auto-switched at
  var AUTO_EVEN = {};              // sectionNum -> WE put this section into Even Panels
  var KEPT_CUT = {};               // sectionNum -> the customer chose Cut Panel themselves
  var CHOSE_EVEN = {};             // sectionNum -> the customer chose Even Panels themselves
  var currentSection = null;
  var LAST_GAP = {};               // sectionNum -> mm the last solved Cut Panel layout could not fill

  function num(x) { var n = parseFloat(x); return isFinite(n) ? n : 0; }
  function isArr(x) { return Object.prototype.toString.call(x) === '[object Array]'; }
  function sum(a) { var t = 0, i; for (i = 0; i < a.length; i++) t += num(a[i]); return t; }
  function same(a, b) {
    if (!isArr(a) || !isArr(b) || a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) if (num(a[i]) !== num(b[i])) return false;
    return true;
  }
  function fill(n, v) { var o = [], i; for (i = 0; i < n; i++) o.push(v); return o; }

  /* ------------------------------------------------------------------------ *
   * 1. the layout
   * ------------------------------------------------------------------------ */

  /* -> { widths, posts, gap } or null when this is not ours to touch */
  function plan(result) {
    var sheet = num(result.panelWidth);
    var widths = result.panelWidths;
    var L = num(result.originalSectionLength);
    var post = num(result.gapSpacing) || 50;
    var posts = num(result.totalGaps);
    var deductions = num(result.deductions);
    if (!isArr(widths) || widths.length < 1 || !(sheet > 0) || !(L > 0) || !(posts > 0)) return null;

    var i, spanNow = 0;
    for (i = 0; i < widths.length; i++) {
      var w = num(widths[i]);
      if (!(w > 0)) return null;
      if (w > sheet) return null;                    /* oversized last panel — Pete's kit-up rule */
      spanNow += w;
    }

    /* What the posts and the gate/rake take out of the run.
       `deductions` is the honest source, but _calculateMixedSpread returns a leaner object
       with no deductions and no perfomesh at all - which is why the first version of this
       stood down on exactly the mixed-sheet layouts it was written to replace, and 12300
       on 3000 sheets kept drawing 3000+3000+3000+2000+1000. Fall back to what the run
       itself accounts for. A layout that is already short folds that shortfall in here and
       simply re-plans to itself, which is harmless. */
    var fixedMM = (deductions > 0) ? (deductions - posts * post)
                                   : (L - spanNow - posts * post);
    if (!(fixedMM >= 0)) return null;

    var n = widths.length;
    var best = null;
    for (var k = 1; k <= 80; k++) {
      var postsK = posts + (k - n);
      if (postsK < 2) continue;
      var spanK = L - fixedMM - postsK * post;
      if (k * sheet > spanK) break;                  /* k full panels no longer fit */
      best = { full: k, posts: postsK, span: spanK };
    }
    if (!best) return null;

    /* one more panel, if what is left is a cut panel worth making */
    var postsB = best.posts + 1;
    var spanB = L - fixedMM - postsB * post;
    var cut = spanB - best.full * sheet;
    var out = (cut >= MIN_CUT)
      ? { widths: fill(best.full, sheet).concat([cut]), posts: postsB, gap: 0 }
      : { widths: fill(best.full, sheet), posts: best.posts, gap: best.span - best.full * sheet };

    /* Changing the panel count moves a post, and on a gated or raked section the gate's
       own placement is solved against that post run. Redistributing widths within the
       same count is safe there; changing the count is not, and that case has not been
       measured. Plain runs only. */
    if (out.widths.length !== n && fixedMM !== 0) return null;

    out.fixedMM = fixedMM; out.post = post; out.L = L; out.sheet = sheet;
    return out;
  }

  /* the layout must account for every millimetre of the section */
  function verify(p) {
    if (!p || !p.widths.length) return false;
    if (p.gap < 0) return false;
    return sum(p.widths) + p.posts * p.post + p.fixedMM + p.gap === p.L;
  }

  function relabel(w, sheet) {
    return { footprint: w, sheet: w, is_cut: w !== sheet,
             kit: w === sheet ? (sheet + 'mm') : 'filler' };
  }

  function apply(result, p) {
    var sheet = p.sheet, n = p.widths.length;
    result.panelWidths = p.widths;
    result.totalGaps = p.posts;
    result.totalGapWidth = p.posts * p.post;
    result.deductions = p.posts * p.post + p.fixedMM;
    result.panel_space = p.L - result.deductions;
    result.gapSpacings = fill(p.posts, p.post);
    result.panel_count = n;
    result.regularPanels = n;
    result.numPanels = n;
    result.numVariablePanels = n;
    result.remaining = p.gap;
    result.valid = true;
    if (result.perfomesh) {
      result.perfomesh.panels = p.widths.map(function (w) { return relabel(w, sheet); });
      result.perfomesh.panel_count = n;
      result.perfomesh.panel_space = result.panel_space;
      result.perfomesh.deductions = result.deductions;
      result.perfomesh.filler = p.gap;
      result.perfomesh.checksOut = true;
    }
    if (isArr(result.panels)) {
      result.panels = p.widths.map(function (w) { return relabel(w, sheet); });
    }
  }

  function repair(result) {
    if (!result || typeof result !== 'object' || result.valid === false) return result;
    var before = isArr(result.panelWidths) ? result.panelWidths.slice() : null;
    if (!before) return result;

    var p = plan(result);
    if (p && verify(p)) {
      if (SIDE[currentSection] === 'start') p.widths = p.widths.slice().reverse();
      if (!same(p.widths, before)) {
        apply(result, p);
        try {
          console.log(LOG, 'section', currentSection, before.join('+'), '->', p.widths.join('+'),
                      '(' + p.posts + ' posts, sheet ' + p.sheet + 'mm, run ' + p.L + 'mm' +
                      (p.gap ? ', ' + p.gap + 'mm gap' : '') + ')');
        } catch (e) {}
      }
      LAST_GAP[currentSection] = p.gap;
      return result;
    }

    /* not ours to re-plan — the cut side is still ours to honour */
    if (SIDE[currentSection] === 'start') {
      var flipped = before.slice().reverse();
      if (!same(flipped, before)) {
        result.panelWidths = flipped;
        if (result.perfomesh && isArr(result.perfomesh.panels) &&
            result.perfomesh.panels.length === flipped.length) {
          result.perfomesh.panels = flipped.map(function (w) {
            return relabel(w, num(result.panelWidth));
          });
        }
      }
    }
    return result;
  }

  /* ------------------------------------------------------------------------ *
   * 2. hooking the calculator
   * ------------------------------------------------------------------------ */

  function calc() { return window.glassCalculator || window.glassFencingCalculator || null; }

  function wrapSectionAware(gc, name) {
    /* calculateSpread is not told which section it is solving, but these callers are. */
    var fn = gc[name];
    if (typeof fn !== 'function') return;
    gc[name] = function (sectionNum) {
      var prev = currentSection;
      currentSection = sectionNum;
      try { return fn.apply(this, arguments); }
      finally { currentSection = prev; }
    };
  }

  function hook() {
    var gc = calc();
    if (!gc || gc.__cfCutPanelWrapped) return !!gc;
    var proto = Object.getPrototypeOf(gc);
    var spread = proto && proto.calculateSpread;
    if (typeof spread !== 'function') return false;

    gc.calculateSpread = function () {
      var r = spread.apply(this, arguments);
      try { return repair(r); }
      catch (e) { try { console.warn(LOG, 'repair skipped', e); } catch (e2) {} return r; }
    };
    wrapSectionAware(gc, '_recalcAndRender');
    wrapSectionAware(gc, 'updateCalculationResults');
    gc.__cfCutPanelWrapped = 1;
    try { console.log(LOG, 'installed'); } catch (e) {}
    return true;
  }

  function resolve(n) {
    /* The blank-and-retype trick does NOT re-solve a section: the length ends up where it
       started and the calculator treats that as no change - measured, the drawing kept the
       old order and nothing logged. Its own recalculation does work. */
    var gc = calc();
    var key = /^\d+$/.test(String(n)) ? parseInt(n, 10) : n;
    if (!gc) return;
    try {
      if (typeof gc._recalcAndRender === 'function') gc._recalcAndRender(key);
      if (typeof gc.updateCalculationResults === 'function') gc.updateCalculationResults(key);
    } catch (e) { try { console.warn(LOG, 'recalc', e); } catch (e2) {} }
  }

  /* ------------------------------------------------------------------------ *
   * 3. Even Panels when the run cannot be filled exactly
   * ------------------------------------------------------------------------ */

  function modeOf(n) {
    var r = document.querySelector('input[name="section-' + n + '-panel-mode"]:checked');
    return r ? r.value : null;
  }

  function lengthOf(n) {
    var el = document.getElementById('section-' + n + '-length');
    return el ? (parseInt(el.value, 10) || 0) : 0;
  }

  var weAreSetting = false;

  /* Setting the radio is not enough on its own.
     Measured: clicking the label switched the control to Even Panels, but the section's
     own state.panelMode stayed "cut", so the next re-render restored Cut Panel and the
     switch silently undid itself - and because SWITCHED had already been stamped for that
     length, it never tried again. Whether it stuck depended on the order the fields were
     filled in, which is exactly the kind of flakiness that must not ship. So write the
     state as well as the control. */
  function setMode(n, value) {
    var r = document.querySelector('input[name="section-' + n + '-panel-mode"][value="' + value + '"]');
    if (!r) return false;
    var gc = calc();
    var key = /^\d+$/.test(String(n)) ? parseInt(n, 10) : n;
    var already = r.checked;
    weAreSetting = true;
    try {
      if (!already) {
        var lab = (r.id && document.querySelector('label[for="' + r.id + '"]')) || r.closest('label');
        (lab || r).click();
        if (!r.checked) r.checked = true;
        r.dispatchEvent(new Event('change', { bubbles: true }));
      }
      try {
        var s = gc && gc.state && gc.state.sections && (gc.state.sections[key] || gc.state.sections[n]);
        if (s) {
          if (s.panelMode === value && already) return false;
          s.panelMode = value;
        } else if (already) {
          return false;
        }
      } catch (e) { if (already) return false; }
    } finally {
      weAreSetting = false;
    }
    return true;
  }

  function autoEven() {
    for (var n in LAST_GAP) {
      if (!Object.prototype.hasOwnProperty.call(LAST_GAP, n)) continue;
      var L = lengthOf(n);
      if (!L) continue;
      if (!(LAST_GAP[n] > 0)) continue;            /* the run fills exactly — nothing to do */
      if (modeOf(n) !== 'cut') continue;
      if (KEPT_CUT[n] === L) continue;             /* the customer chose Cut Panel at this length */
      if (SWITCHED[n] === L) continue;             /* already offered at this length */
      SWITCHED[n] = L;
      if (setMode(n, 'even')) {
        AUTO_EVEN[n] = 1;
        try { console.log(LOG, 'section', n, 'cannot fill ' + L + 'mm exactly (' +
                          LAST_GAP[n] + 'mm short) — switched to Even Panels'); } catch (e) {}
        resolve(n);
      }
    }
  }

  /* a mode change we did not cause is the customer's decision */
  document.addEventListener('change', function (e) {
    try {
      var t = e.target;
      if (!t || !t.name || !/-panel-mode$/.test(t.name)) return;
      if (weAreSetting) return;
      var m = /section-(\d+)-panel-mode/.exec(t.name);
      if (!m) return;
      if (t.value === 'cut') { KEPT_CUT[m[1]] = lengthOf(m[1]); AUTO_EVEN[m[1]] = 0; }
      else if (t.value === 'even') { CHOSE_EVEN[m[1]] = 1; AUTO_EVEN[m[1]] = 0; }
    } catch (err) {}
  }, true);

  /* Our switch belongs to the length that caused it.
     Measured on the preview theme: type 12300 on 3000 sheets, which cannot fill exactly,
     then change to 7300, which can. The section stayed on Even Panels and quoted three
     even 2367mm panels at $2,217.06 instead of 3000+3000+1100 at $2,012.48 - the customer
     paying $204.58 more because of a length they had already replaced. So when the length
     changes, put a section WE switched back to Cut Panel and let it be judged afresh. A
     customer who picked Even Panels themselves keeps it.

     "Changes" has to mean changes to a DIFFERENT length, not merely that the field was
     touched.  Clearing a field to retype it fires change with an empty value, and typing
     the same number back does not make the calculator re-solve - it ends where it started,
     so nothing re-offers Even Panels and the section is left on Cut Panel purely because
     the customer selected the text.  Measured: 12300 on 3000 sheets switched to Even, then
     blank-and-retype-12300 sat on Cut Panel.  So ignore an empty or unchanged value and
     revert only when the number the switch was made for is genuinely gone. */
  document.addEventListener('change', function (e) {
    try {
      var t = e.target;
      if (!t || !t.id) return;
      var m = /^section-(\d+)-length$/.exec(t.id);
      if (!m) return;
      var n = m[1];
      if (!AUTO_EVEN[n] || CHOSE_EVEN[n]) return;
      if (modeOf(n) !== 'even') return;
      var now = parseInt(t.value, 10) || 0;        /* same reading as lengthOf() */
      if (!(now > 0)) return;                      /* cleared, mid-edit - not a new length */
      if (SWITCHED[n] && now === SWITCHED[n]) return;   /* same length, retyped */
      AUTO_EVEN[n] = 0;
      SWITCHED[n] = null;
      if (setMode(n, 'cut')) {
        try { console.log(LOG, 'section', n, 'length changed — back to Cut Panel to be judged afresh'); } catch (err2) {}
        resolve(n);
      }
    } catch (err) {}
  }, true);

  /* ------------------------------------------------------------------------ *
   * 4. the Cut Panel Position control
   * ------------------------------------------------------------------------ */

  function sectionOf(el) {
    var m = /section-(\d+)-/.exec(el.getAttribute('name') || el.id || '');
    return m ? m[1] : null;
  }

  function addSideControls() {
    var radios = document.querySelectorAll('input[name$="-panel-mode"]');
    for (var i = 0; i < radios.length; i++) {
      var n = sectionOf(radios[i]);
      if (!n || document.getElementById('cf-cut-side-' + n)) continue;

      var host = radios[i].closest('.form-group, .field, .input-group, div');
      for (var up = 0; up < 3 && host && host.parentElement &&
           host.querySelectorAll('input[name$="-panel-mode"]').length < 2; up++) {
        host = host.parentElement;
      }
      if (!host || !host.parentElement) continue;

      var wrap = document.createElement('div');
      wrap.className = 'cf-cut-side-wrap';
      wrap.style.marginTop = '14px';

      var lab = document.createElement('label');
      lab.setAttribute('for', 'cf-cut-side-' + n);
      lab.textContent = 'Cut Panel Position';
      lab.style.display = 'block';
      lab.style.fontWeight = '600';
      lab.style.marginBottom = '6px';

      var sel = document.createElement('select');
      sel.id = 'cf-cut-side-' + n;
      sel.className = 'cf-cut-side';
      sel.style.width = '100%';
      sel.style.padding = '10px';
      sel.innerHTML = '<option value="end">Cut panel at the end (right)</option>' +
                      '<option value="start">Cut panel at the start (left)</option>';
      sel.value = SIDE[n] === 'start' ? 'start' : 'end';
      (function (num_) {
        sel.addEventListener('change', function (e) {
          SIDE[num_] = e.target.value;
          resolve(num_);
        });
      })(n);

      wrap.appendChild(lab);
      wrap.appendChild(sel);
      host.parentElement.insertBefore(wrap, host.nextSibling);
    }
  }

  /* ------------------------------------------------------------------------ *
   * 5. Configuration Summary row order
   * ------------------------------------------------------------------------ */

  var ORDER = ['length', 'panels', 'rake', 'posts', 'gate', 'hinge type', 'latch type'];

  function rowKey(row) {
    var first = row.firstElementChild;
    var t = String((first ? first.textContent : row.textContent) || '')
              .replace(/\s+/g, ' ').trim().toLowerCase().replace(/:$/, '');
    if (t === 'raked') t = 'rake';
    return ORDER.indexOf(t) >= 0 ? t : null;
  }

  function reorderSummaries() {
    var boxes = document.querySelectorAll('.section-summary');
    for (var b = 0; b < boxes.length; b++) {
      var kids = boxes[b].children;
      for (var c = 0; c < kids.length; c++) {
        var host = kids[c];
        if (host.getAttribute('data-cf-ordered') === '1') continue;
        var rows = [], i, k, found = 0;
        for (i = 0; i < host.children.length; i++) {
          k = rowKey(host.children[i]);
          rows.push({ el: host.children[i], key: k });
          if (k) found++;
        }
        if (found < 3) continue;
        rows.sort(function (x, y) {
          return (x.key ? ORDER.indexOf(x.key) : 99) - (y.key ? ORDER.indexOf(y.key) : 99);
        });
        host.setAttribute('data-cf-ordered', '1');
        for (i = 0; i < rows.length; i++) host.appendChild(rows[i].el);
      }
    }
  }

  /* ------------------------------------------------------------------------ *
   * wiring
   * ------------------------------------------------------------------------ */

  var pending = null;
  function tick() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      try { hook(); } catch (e) { try { console.warn(LOG, 'hook', e); } catch (e2) {} }
      try { addSideControls(); } catch (e) { try { console.warn(LOG, 'controls', e); } catch (e2) {} }
      try { reorderSummaries(); } catch (e) { try { console.warn(LOG, 'summary', e); } catch (e2) {} }
      try { autoEven(); } catch (e) { try { console.warn(LOG, 'even', e); } catch (e2) {} }
    }, 300);
  }

  function start() {
    tick();
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      try { if (hook() || tries > 40) clearInterval(poll); } catch (e) {}
    }, 500);
    try {
      new MutationObserver(tick).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
